import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { NotificationService } from '@app/finxone-web-frontend/app/lib/services/notification-service/notification-service.service';
import { WorkflowFormGuard } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-form.guard';
import { redirectToPageWithUrl } from '@app/finxone-web-frontend/app/lib/utils/navbar-navigation.utils';
import {
  buildNavBarHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'finxone-web-frontend-custom-navbar',
  templateUrl: './custom-navbar.component.html',
  styleUrl: './custom-navbar.component.css',
  standalone: true,
  imports: [CommonModule],
})
export class CustomNavbarComponent implements OnInit, AfterViewInit, OnDestroy {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  private _htmlContent = 'no html inserted';
  @Input()
  set htmlContent(value: string) {
    if (value !== this._htmlContent) {
      this._htmlContent = value;
      this.renderHandlebarContent();
    }
  }
  get htmlContent(): string {
    return this._htmlContent;
  }
  @ViewChild('contentContainer', { static: true }) contentContainer!: ElementRef<HTMLElement>;
  private clickListener: (e: Event) => void;
  public renderedContent: SafeHtml = '';
  private routerEventsSub: any;
  private previousRenderedHtml = '';
  private template: HandlebarsTemplateDelegate | null = null;

  constructor(
    private router: Router,
    private store: Store,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private injector: Injector,
    private notificationService: NotificationService,
  ) {}

  ngOnInit() {
    this.renderHandlebarContent();
    // Listen for route changes to update active links
    this.routerEventsSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateActiveLinks();
      }
    });
  }

  ngAfterViewInit(): void {
    const container = this.contentContainer?.nativeElement;

    this.clickListener = (event: Event) => {
      let target = event.target as HTMLElement;

      // ✅ Walk up the DOM tree in case a <span> inside the <a> was clicked
      while (target && target.tagName.toLowerCase() !== 'a') {
        target = target.parentElement as HTMLElement;
      }

      if (target && target.tagName.toLowerCase() === 'a') {
        const anchor = target as HTMLAnchorElement;
        const href = anchor.getAttribute('href');

        // Only intercept internal links (no http:// or https://)
        if (href && !href.startsWith('http')) {
          const mouseEvent = event as MouseEvent;
          if (mouseEvent.button === 0 && !mouseEvent.ctrlKey && !mouseEvent.metaKey) {
            event.preventDefault();
            // Disable form validation check for navigating to the set page
            WorkflowFormGuard.disableFormValidation();
            redirectToPageWithUrl(href, this.injector);
          }
        }
      }
    };

    container.addEventListener('click', this.clickListener);
    // Initial active link update
    setTimeout(() => this.updateActiveLinks());
  }

  ngOnDestroy(): void {
    this.contentContainer?.nativeElement.removeEventListener('click', this.clickListener);
    if (this.routerEventsSub) {
      this.routerEventsSub.unsubscribe();
    }
  }

  private renderHandlebarContent() {
    this.template = Handlebars.compile(this.htmlContent);
    buildNavBarHandleBarModel(this.iconCacheService, this.store, this.notificationService)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        if (!this.template) return;
        const html = filterIfHandlebarNotRendered(this.template(model));
        const newContent = this.sanitizer.bypassSecurityTrustHtml(html);

        if (html === this.previousRenderedHtml) return;

        this.previousRenderedHtml = html;
        this.renderedContent = newContent;
        this.cdr.markForCheck();
        setTimeout(() => {
          this.updateActiveLinks();
          this.executeInlineScripts();
        });
      });
  }

  // Executes <script> tags inside the container
  private executeInlineScripts() {
    const container = this.contentContainer?.nativeElement;
    if (!container) return;
    const scripts = container.querySelectorAll('script');
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script');
      // Copy attributes
      for (let i = 0; i < oldScript.attributes.length; i++) {
        const attr = oldScript.attributes[i];
        if (newScript) newScript?.setAttribute(attr.name, attr.value);
      }
      // Copy script content
      newScript.text = oldScript.text;
      // Replace old script with new one to trigger execution
      oldScript.parentNode?.replaceChild(newScript, oldScript);
    });
  }

  private updateActiveLinks() {
    // adds active class to the current link based on the router url
    const container = this.contentContainer?.nativeElement;
    if (!container) return;
    const links = container.querySelectorAll('a[href]');
    const currentPath = this.router.url.split('?')[0];
    links.forEach((link) => {
      const href = (link as HTMLAnchorElement).getAttribute('href');
      if (href && !href.startsWith('http')) {
        if (href === currentPath) {
          link.classList.add('active');
        } else {
          link.classList.remove('active');
        }
      }
    });
  }
}
